import { isAxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Layout } from "../components/Layout";
import faculties from "../data/faculties.json";
import semesters from "../data/semesters.json";
import { EmailTemplate, EmailTemplateList } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";
import { Toast } from "../services/toast-service";
import AcademicEmailComposerForm from "./AcademicEmailComposer/AcademicEmailComposerForm";
import AcademicEmailComposerFormThankYou from "./AcademicEmailComposer/AcademicEmailComposerForm.ThankYou";
import {
  AcademicEmailComposerFormValues,
  UnitOfStudy,
} from "./AcademicEmailComposer/AcademicEmailComposerForm.Types";

interface AcademicEmailComposerProps {
  userInfo: UserInfo;
}

// This will be default for time being
const defaultSendWhen = "welcome";
const defaultCCReplyTo = "student.success@sydney.edu.au";

const AcademicEmailComposer = ({ userInfo }: AcademicEmailComposerProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateList>([]);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>();

  const { handleSubmit, watch, control, setValue, reset } =
    useForm<AcademicEmailComposerFormValues>({
      defaultValues: {
        faculty: "",
        semester: "",
        unitOfStudy: "",
        emailTemplate: "",
        subject: "",
        sendWhen: defaultSendWhen,
        senderEmail: userInfo?.Email,
        replyTo: userInfo?.Email,
        ccReplyTo: defaultCCReplyTo,
        salutation: "Hi",
        personalMessage: "",
        signature: "",
        preferredName: "{{PreferredName}}",
      },
    });
  const formValues = watch();
  const selectedFaculty = watch("faculty");

  const unitsOfStudy: UnitOfStudy[] =
    semesters.find((semester) => semester.value === formValues.semester)
      ?.unitsOfStudy ?? [];

  const cleanHTML = (html: string) => {
    // remove color and background-color styles
    // ref - https://docs.aws.amazon.com/waf/latest/developerguide/aws-managed-rule-groups-baseline.html
    return html.replace(/style="[^"]*"/g, "");
  };

  const onSubmit = async () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      setLoading(true);
      try {
        await notificationService.submitForReview({
          emailSubject: formValues.subject,
          emailTemplate: emailTemplate?.name ?? "",
          fromEmail: formValues.senderEmail,
          replyToEmail: formValues.replyTo,
          emailTemplateVariables: {
            Faculty: selectedFaculty,
            Semester: formValues.semester,
            UnitOfStudyCode: formValues.unitOfStudy,
            UnitOfStudyName: unitsOfStudy?.find(
              (unit) => unit.value === formValues.unitOfStudy,
            )?.label,
            EmailSignature: cleanHTML(formValues.signature),
            Salutation: cleanHTML(formValues.salutation),
            PersonalMessage: cleanHTML(formValues.personalMessage),
            EmailSubject: formValues.subject,
          },
        });
        // console.log(response);
        setShowThankYou(true);
      } catch (err: unknown) {
        console.error("onSubmit error:", err);

        let message = `Something went wrong. Please try again. ${err}`;
        if (isAxiosError(err)) {
          if (err.response?.data?.message) {
            message = err.response.data.message;
          } else if (err.response?.status === 400) {
            message = "Bad request. Please check the input fields.";
          } else if (err.response?.status === 401) {
            message = "Unauthorized. Please log in again.";
          } else if (err.response?.status === 403) {
            message =
              "The content provided was invalid. Please review and try again.";
          } else if (err.response?.status === 500) {
            message = "Server error. Please try again later.";
          }
        }

        Toast("Error!", message, "danger");
      }
    }
    setLoading(false);
  };

  const getEmailTemplateVariables = (
    values: AcademicEmailComposerFormValues,
  ) => ({
    Semester: values.semester,
    UnitOfStudyCode: values.unitOfStudy,
    UnitOfStudyName: unitsOfStudy?.find(
      (unit) => unit.value === values.unitOfStudy,
    )?.label,
    Salutation: values.salutation
      ? `<span class="bg-slate-100 py-2">${values.salutation}</span>`
      : "",
    EmailSignature: values.signature
      ? `<div class="bg-slate-100 py-2">${values.signature}</div>`
      : "",
    PersonalMessage: values.personalMessage
      ? `<div class="bg-slate-100 py-2">${values.personalMessage}</div>`
      : "",
    PreferredName: values.preferredName
      ? `<span class="bg-slate-100 py-2 cursor-not-allowed" title="Automatically populated with student's preferred name.">${values.preferredName}</span>`
      : "",
  });

  return (
    <Layout
      userInfo={userInfo}
      hideNavigationLinks
      loading={loading}
      className="flex flex-col h-dvh bg-light"
    >
      {showThankYou ? (
        <AcademicEmailComposerFormThankYou
          setCurrentStep={setCurrentStep}
          setShowThankYou={setShowThankYou}
          reset={reset}
          formValues={formValues}
          getEmailTemplateVariables={getEmailTemplateVariables}
          emailTemplate={emailTemplate as EmailTemplate}
          unitOfStudy={
            unitsOfStudy.find(
              (unit) => unit.value === formValues.unitOfStudy,
            ) ?? { value: "", label: "" }
          }
          faculty={
            formValues.faculty
              ? faculties.find((faculty) => faculty.id === selectedFaculty)
                  ?.name ?? ""
              : ""
          }
        />
      ) : (
        <AcademicEmailComposerForm
          getEmailTemplateVariables={getEmailTemplateVariables}
          currentStep={currentStep}
          setLoading={setLoading}
          setShowThankYou={setShowThankYou}
          userInfo={userInfo}
          emailTemplates={emailTemplates}
          emailTemplate={emailTemplate as EmailTemplate}
          setEmailTemplate={setEmailTemplate}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
          control={control}
          setValue={setValue}
          formValues={formValues}
          unitsOfStudy={unitsOfStudy}
          selectedFaculty={selectedFaculty}
          setCurrentStep={setCurrentStep}
          setEmailTemplates={setEmailTemplates}
        />
      )}
    </Layout>
  );
};

export default AcademicEmailComposer;
